import React, {
  ChangeEvent,
  FormEvent,
  KeyboardEvent,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { styled } from 'styled-components';
import { DazzleAddress, DazzleUser, Order, OrderStatus, OrderType } from '@dazzle/api-client';
import { getDatabase, onValue, ref, Unsubscribe } from 'firebase/database';
import TextareaAutosize from 'react-textarea-autosize';
import { v4 as uuidv4 } from 'uuid';

import { DesktopShow, DeviceQueries } from 'Common/components/styled';
import SubmittingOverlay from 'Common/components/SubmittingOverlay';
import BlueSpinner from 'Common/components/BlueSpinner';
import { useModalContext } from 'Common/contexts/ModalContext';
import Modal from 'Common/components/Modal';
import { NotificationOutlet, useNotificationContext } from 'Common/contexts/NotificationContext';
import { BannerLevel } from 'Common/components/Banner';
import {
  orderList,
  orderSendPaymentLink,
  orderStripeClear,
  userGetInternal,
  userUpdateInternal
} from 'Common/api/DazzleInternalService';
import KebabIcon from 'Common/components/icons/KebabIcon';
import { logSumoEvent, ULogApplication, ULogSeverity, ULogTag } from 'Common/api/SumoLogic';

import CompassHeader, { PageType } from 'Compass/components/CompassHeader';
import { OrderTypeFilterNames, useOrdersContext } from 'Compass/contexts/OrdersContext';
import OrderStepper from 'Compass/components/OrderStepper';
import { formatDate, getStatusColor, getStatusLabel, getValidNextStatuses } from 'Compass/utils/orderUtils';
import { StyledSelect } from 'Compass/components/styled/Select';
import { StyledTextarea } from 'Compass/components/styled/Textarea';
import { Button, ButtonTheme } from 'Compass/components/styled/Button';
import { StyledInput } from 'Compass/components/styled/Input';
import OrderConversation from 'Compass/components/orders/OrderConversation';
import { app } from 'Compass/api/Firebase';
import { Page } from 'Compass/core/MainLayout';
import PlusCircle from 'Compass/components/icons/PlusCircle';
import ImageUrlModal from 'Compass/components/modals/ImageUrlModal';
import Dropdown, { DropdownItem } from 'Compass/components/Dropdown';
import { useUserContext } from 'Compass/contexts/UserContext';
import EditAddressesModal from 'Compass/components/modals/EditAddressesModal';

const OrderPage = () => {
  const navigate = useNavigate();
  const { orderId } = useParams();
  const ordersContext = useOrdersContext();
  const modalContext = useModalContext();
  const notificationContext = useNotificationContext();
  const userContext = useUserContext();
  // const lastModifiedTime = useFirebaseRealtimeDatabase<string>(`dazzle/order/${orderId}/data`, app);

  const [order, setOrder] = useState(orderId ? ordersContext.getOrder(orderId) : undefined);
  const [loading, setLoading] = useState(order === undefined);
  const [user, setUser] = useState<DazzleUser>();
  const [addresses, setAddresses] = useState<DazzleAddress[]>([]);
  const [inferredAddresses, setInferredAddresses] = useState<DazzleAddress[]>([]);
  const [otherOpenOrders, setOtherOpenOrders] = useState<Order[]>([]);
  const [priceStr, setPriceStr] = useState(`${order?.final_price ?? ''}`);
  const [newMessage, setNewMessage] = useState('');
  const [messageImageUrl, setMessageImageUrl] = useState<string | undefined>(undefined);
  const [paymentRequestEnabled, setPaymentRequestEnabled] = useState(true);
  const [conciergeSaved, setConciergeSaved] = useState(!!order?.assigned_to);
  const [submitting, setSubmitting] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);

  const userLoading = useRef(false);
  const userRtdUnsubscribe = useRef<Unsubscribe | undefined>(undefined);

  useEffect(() => {
    if (order && !priceStr && order.final_price !== null) {
      setPriceStr(`${order.final_price}`);
    }
  }, [order, priceStr]);

  const fetchUser = useCallback(async (userId: string) => {
    userLoading.current = true;
    try {
      const response = await userGetInternal(userId);
      if (response?.user) {
        if (user && !user?.has_payment_setup && response.user.has_payment_setup) {
          notificationContext.showBanner({
            id: uuidv4(),
            level: BannerLevel.INFO,
            contents: 'User has supplied their payment information',
            duration: 15000
          });
        }

        setUser(
          response.user
        );
        if (response.user.preferences?.addresses) {
          setAddresses(
            response.user.preferences.addresses
          );
        }
      }
      if (response?.addresses) {
        setInferredAddresses(
          response.addresses
        );
      }
    } catch (e) {
      notificationContext.showBanner({
        level: BannerLevel.ERROR,
        contents: `Error loading user: ${e.message ?? JSON.stringify(e)}.`
      });
    }
    userLoading.current = false;
  }, [notificationContext, user]);

  const fetchOtherOpenOrders = useCallback(async (userId: string) => {
    const response = await orderList({
      userIdFilter: userId,
      statusFilters: [
        OrderStatus.ConfirmedByUser,
        OrderStatus.PaymentCompleted,
        OrderStatus.PaymentFailed,
        OrderStatus.OrderCompleted
      ]
    });

    if (response?.orders) {
      setOtherOpenOrders(response.orders.filter(order => order.order_id !== orderId));
    }
  }, [orderId]);

  const subscribeToUser = useCallback(
    (userId: string) => {
      const db = getDatabase(app);
      const valueRef = ref(db, `/dazzle/user/${userId}`);

      userRtdUnsubscribe.current = onValue(valueRef, () => {
        void fetchUser(userId);
        void fetchOtherOpenOrders(userId);
      });
    },
    [fetchOtherOpenOrders, fetchUser]
  );

  const fetchAllUserData = useCallback(
    (userId: string) => {
      if (!userLoading.current) {
        void fetchUser(userId);
        void fetchOtherOpenOrders(userId);
      }
    }, [fetchUser, fetchOtherOpenOrders]
  )

  useEffect(() => {
    const fetchOrder = async () => {
      if (orderId) {
        try {
          const newOrder = await ordersContext.fetchOrder(orderId);
          if (newOrder) {
            setOrder(newOrder);
            setConciergeSaved(!!newOrder.assigned_to);
            subscribeToUser(newOrder.user_id);
            fetchAllUserData(newOrder.user_id);
          }
        } catch (e) {
          void logSumoEvent({
            app: ULogApplication.COMPASS,
            severity: ULogSeverity.INFO,
            tag: ULogTag.Orders,
            userId: userContext.name,
            message: `[OrderPage] Error fetching order (${orderId}): ${e.message ?? e}`,
          });

          notificationContext.showBanner({
            level: BannerLevel.ERROR,
            contents: `An error occurred while fetching the order: ${e.message ?? e}`,
          });
        } finally {
          setLoading(false);
        }
      }
    };

    if (order === undefined || order.order_id !== orderId) {
      setLoading(true);
      setOrder(undefined);
      setOtherOpenOrders([]);
      void fetchOrder();
    } else if (order && !user) {
      fetchAllUserData(order.user_id);
    }

    return () => {
      if (userRtdUnsubscribe.current) {
        userRtdUnsubscribe.current();
      }
    };
  }, [
    orderId,
    order,
    fetchOtherOpenOrders,
    fetchUser,
    notificationContext,
    ordersContext,
    subscribeToUser,
    userContext.name,
    user,
    fetchAllUserData,
  ]);

  const onDescriptionChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    if (order !== undefined) {
      order.product_description = e.currentTarget.value;
      setOrder({ ...order });
    }
  };

  const onConciergeChange = (e: ChangeEvent<HTMLSelectElement>) => {
    if (order !== undefined) {
      order.assigned_to = e.currentTarget.value;
      setOrder({ ...order });
    }
  };

  const onPriceChange = (e: ChangeEvent<HTMLInputElement>) => {
    setPriceStr(e.currentTarget.value);
    if (order !== undefined) {
      order.final_price = +e.currentTarget.value;
      setOrder({ ...order });
    }
  };

  const chargeCustomer = async () => {
    if (order !== undefined) {
      void logSumoEvent({
        app: ULogApplication.COMPASS,
        severity: ULogSeverity.INFO,
        tag: ULogTag.Orders,
        userId: userContext.name,
        message: `[OrderPage] Charging customer on order (${orderId}): $${order.final_price}`
      });

      setSubmitting(true);
      const newOrder: Order = {
        ...order,
        status: OrderStatus.PaymentCompleted
      };
      try {
        await ordersContext.placeOrder(order);
      } catch (e) {
        void logSumoEvent({
          app: ULogApplication.COMPASS,
          severity: ULogSeverity.ERROR,
          tag: ULogTag.Orders,
          userId: userContext.name,
          message: `[OrderPage] Error charging customer on order (${orderId}): ${e.message ?? e}`
        });

        newOrder.status = OrderStatus.PaymentFailed;
        notificationContext.showBanner({
          level: BannerLevel.ERROR,
          contents: `Something went wrong charging user: ${e.message ?? e}`
        });

        setOrder(newOrder);
        setSubmitting(false);

        return;
      }

      await onSave(OrderStatus.PaymentCompleted);
    }
  };

  const onNotesChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    if (order !== undefined) {
      setOrder({
        ...order,
        notes: e.currentTarget.value
      });
    }
  };

  const onShippingAddressChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    if (order !== undefined) {
      setOrder({
        ...order,
        shipping_address: e.currentTarget.value
      });
    }
  };

  const onSave = async (status: OrderStatus) => {
    if (order === undefined) {
      return;
    }
    const newOrder = {
      ...order,
      status
    };

    void logSumoEvent({
      app: ULogApplication.COMPASS,
      severity: ULogSeverity.INFO,
      tag: ULogTag.Orders,
      userId: userContext.name,
      message: `[OrderPage] Saving order (${orderId}). "${order.status}" -> "${status}"`
    });

    setSubmitting(true);
    try {
      await ordersContext.updateOrder(newOrder);
      setConciergeSaved(!!newOrder.assigned_to);
    } catch (e) {
      void logSumoEvent({
        app: ULogApplication.COMPASS,
        severity: ULogSeverity.ERROR,
        tag: ULogTag.Orders,
        userId: userContext.name,
        message: `[OrderPage] Error saving order (${orderId}): ${e.message ?? e}`
      });

      notificationContext.showBanner({
        level: BannerLevel.ERROR,
        contents: `Something went wrong saving order: ${e.message ?? e}`
      });
    }
    setOrder(newOrder);
    setSubmitting(false);
  };

  if (orderId === undefined) {
    navigate('/orders');
    return;
  }

  let currentStep: number = 0;
  switch (order?.status) {
    case OrderStatus.SuggestionPending:
      currentStep = 1;
      break;
    case OrderStatus.ConfirmedByDazzle:
      currentStep = 2;
      break;
    case OrderStatus.ConfirmedByUser:
      currentStep = 3;
      break;
    case OrderStatus.OrderPending:
      currentStep = 4;
      break;
    case OrderStatus.PaymentCompleted:
      currentStep = 5;
      break;
    case OrderStatus.OrderCompleted:
      currentStep = 6;
      break;
  }

  const sendMessage = async () => {
    if (order?.conversation_id && order?.assigned_to) {
      setNewMessage('');
      setMessageImageUrl(undefined);
      try {
        await ordersContext.replyToUser(orderId, order.conversation_id, newMessage, messageImageUrl);
      } catch (e) {
        notificationContext.showBanner({
          level: BannerLevel.ERROR,
          contents: `Error sending message: ${e.message ?? e}`
        });
      }
    }
  };

  const onReplyFormSubmit = async (e: FormEvent) => {
    e.preventDefault();

    await sendMessage();
  };

  const onReplyKeyUp = async (e: KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();

      await sendMessage();
    }
  };

  const sendPaymentLink = async () => {
    setPaymentRequestEnabled(false);
    await orderSendPaymentLink(orderId);
  };

  const onClearPaymentClick = async () => {
    if (!user) {
      return;
    }
    setIsUserMenuOpen(false);
    if (order && user?.has_payment_setup) {
      await orderStripeClear(order.user_id);
    }
  };

  const onAddressesSave = async (addresses: DazzleAddress[]) => {
    modalContext.hide();

    if (user) {
      setSubmitting(true);
      const newUser: DazzleUser = {
        user_id: user.user_id,
        preferences: {
          ...user.preferences,
          addresses
        }
      };

      await userUpdateInternal(newUser);

      setAddresses(addresses);
      setSubmitting(false);
    }
  }
  const onEditAddressesClick = async () => {
    if (!user) {
      return;
    }
    setIsUserMenuOpen(false);
    modalContext.show({
      contents: <EditAddressesModal inferredAddresses={inferredAddresses} addresses={addresses} onSave={onAddressesSave}/>
    });
  };

  const onImageClick = (imageUrl: string) => {
    modalContext.show({
      contents: <Modal onClickOutside={modalContext.hide} maxWidth="800px">
        <img src={imageUrl}/>
      </Modal>
    });
  };

  const onImageUpload = (imageUrl: string) => {
    setMessageImageUrl(imageUrl);
  };

  const onAddImageClick = () => {
    modalContext.show({
      contents: <ImageUrlModal
        close={modalContext.hide}
        onSave={onImageUpload}
      />
    });
  };

  const replyContent = <ReplyRows data-testid="reply-container">
    <ReplyInputContainer onSubmit={onReplyFormSubmit}>
      <AddImageContainer onClick={onAddImageClick}>
        <PlusCircle/>
      </AddImageContainer>
      {
        messageImageUrl && <SmallUploadedImage src={messageImageUrl} onClick={() => onImageClick(messageImageUrl)}/>
      }
      <ReplyInput
        placeholder="Type a message..."
        value={newMessage}
        onChange={e => setNewMessage(e.currentTarget.value)}
        onKeyUp={onReplyKeyUp}
      />
      <Button
        type="submit"
        disabled={(!newMessage && !messageImageUrl) || !conciergeSaved}
      >Send</Button>
    </ReplyInputContainer>
    {
      <ConversationSecondaryActions>
        <Button
          $theme={user?.has_payment_setup ? ButtonTheme.secondary: ButtonTheme.green}
          onClick={sendPaymentLink}
          disabled={!paymentRequestEnabled || !conciergeSaved}
        >
          {user?.has_payment_setup ? 'Resend' : 'Send'} payment collection link
        </Button>
        {
          !conciergeSaved && <AssignWarning>
            ⚠️ Assign and save a concierge before replying.
          </AssignWarning>
        }
      </ConversationSecondaryActions>
    }
  </ReplyRows>;

  const nextStatuses = order?.status ? getValidNextStatuses(order.status) : [];

  let content: ReactNode;
  if (loading) {
    content = <CenteredContainer><BlueSpinner/></CenteredContainer>;
  } else if (!order) {
    content = <NotificationOutlet/>;
  } else {
    content = <>
      <NotificationOutlet/>
      <DesktopShow>
        <OrderStepper current={currentStep}/>
      </DesktopShow>
      <Card>
        <CardHeader>
          <OrderTitle>Order Details</OrderTitle>
          <StatusBadge $color={getStatusColor(order.status)} data-testid="order-status">
            {getStatusLabel(order.status)}
          </StatusBadge>
        </CardHeader>

        <TwoColumn>
          <Section>
            <SectionTitle>{OrderTypeFilterNames[order.order_type ?? OrderType.Product]} Information</SectionTitle>
            <DataTable>
              <Row>
                <Label htmlFor="order-description">Description</Label>
                <Value>
                  <ReplyInput id="order-description" value={order.product_description} onChange={onDescriptionChange}/>
                </Value>
              </Row>
              <Row>
                <Label htmlFor="order-price">Price</Label>
                <Value>
                  $ <PriceInput id="order-price" value={priceStr} onChange={onPriceChange}/>
                  {
                    user?.has_payment_setup === true &&
                    (order?.status === OrderStatus.OrderPending || order?.status === OrderStatus.PaymentFailed) &&
                    <Button $theme={ButtonTheme.green} onClick={chargeCustomer}>Charge customer</Button>
                  }
                </Value>
              </Row>
            </DataTable>
          </Section>
          {order.photo_url && <ProductImage src={order.photo_url} onClick={() => onImageClick(order.photo_url ?? '')}/>}
        </TwoColumn>

        <Divider/>

        <Section>
          <SectionTitle>
            <TitleRow>
              <div>User Details</div>
              <IconWrapper role="menu" title="User menu">
                <KebabIcon onClick={() => setIsUserMenuOpen(true)}/>
              </IconWrapper>
              { isUserMenuOpen && <Dropdown onClose={() => setIsUserMenuOpen(false)}>
                <NotCapitalizedDropdownItem onClick={onClearPaymentClick} $disabled={!user || !user.has_payment_setup}
                                            role="menuitem" data-enabled={user && user.has_payment_setup}>
                  Clear Payment Information
                </NotCapitalizedDropdownItem>
                <NotCapitalizedDropdownItem onClick={onEditAddressesClick} $disabled={!user} role="menuitem"
                                            data-enabled={!!user}>
                  Edit Addresses
                </NotCapitalizedDropdownItem>
              </Dropdown> }
            </TitleRow>
            </SectionTitle>
          <DataTable>
            <Row>
              <Label>User ID</Label>
              <Value>
                <UserId>{order.user_id || 'N/A'}</UserId>
              </Value>
            </Row>
            <Row>
              <Label>Name</Label>
              <Value>
                {user?.full_name}
              </Value>
            </Row>
            {
              inferredAddresses.length > 0 && <Row>
                <Label>Addresses (Inferred)</Label>
                <Value>
                  <AddressList>
                    {inferredAddresses.map((address, i) => <AddressWrapper key={i}>
                      {address.address}
                    </AddressWrapper>)}
                  </AddressList>
                </Value>
              </Row>
            }
            {
              addresses.length > 0 && <Row>
                <Label>Addresses</Label>
                <Value>
                  <AddressList>
                    {addresses.map((address, i) => <AddressWrapper key={i}>
                      {address.address}
                    </AddressWrapper>)}
                  </AddressList>
                </Value>
              </Row>
            }
            <Row>
              <Label>Payment Information</Label>
              <Value>
                {user?.has_payment_setup === true ? 'Collected' : user?.has_payment_setup === false ? 'Not Collected' : ''}
              </Value>
            </Row>
            <Row>
              <Label>Other Open Orders</Label>
              <Value>
                <AddressList>
                {
                  otherOpenOrders.length > 0 ? otherOpenOrders.map(otherOrder => <div key={otherOrder.order_id}>
                    <Link to={`/order/${otherOrder.order_id}`}>{otherOrder.product_description}</Link>
                  </div>) : null
                }
                </AddressList>
              </Value>
            </Row>
          </DataTable>
        </Section>

        <Divider/>

        <Section>
          <SectionTitle>Order Details</SectionTitle>
          <DataTable>
            <Row>
              <Label>Order Placed</Label>
              <Value>{formatDate(order.order_ts)}</Value>
            </Row>
            <Row>
              <Label htmlFor="order-concierge">Assigned To</Label>
              <Value>
                <StyledSelect value={order.assigned_to ?? ''} onChange={onConciergeChange} id="order-concierge">
                  <option value="">- Unassigned -</option>
                  {
                    ordersContext.concierges.map(concierge => <option value={concierge.email} key={concierge.email}>
                      {concierge.name} ({concierge.email})
                    </option>)
                  }
                </StyledSelect>
              </Value>
            </Row>
            <Row>
              <Label htmlFor="order-shipping-address">Shipping Address</Label>
              <Value>
                <StyledTextarea
                  id="order-shipping-address"
                  rows={3}
                  value={order.shipping_address ?? ''}
                  onChange={onShippingAddressChange}
                />
              </Value>
            </Row>
            <Row>
              <Label htmlFor="order-notes">Notes</Label>
              <Value><StyledTextarea id="order-notes" rows={8} value={order.notes ?? ''} onChange={onNotesChange}/></Value>
            </Row>
            <Row>
              <Label>Last Updated</Label>
              <Value>{formatDate(order.last_updated_ts)}</Value>
            </Row>
          </DataTable>
        </Section>

        <Divider/>

        <Section>
          <SectionTitle>Save and Update Status</SectionTitle>
          <ButtonGroup>
            <Button $theme={ButtonTheme.secondary} onClick={() => onSave(order.status)}>Save</Button>
            {
              nextStatuses.map(status => <Button
                key={status}
                onClick={() => onSave(status)}
                $theme={status === OrderStatus.OrderClosed ? ButtonTheme.red : ButtonTheme.blue }
              >
                Save and change to {getStatusLabel(status)}
              </Button>)
            }
          </ButtonGroup>
        </Section>
      </Card>

      <Card>
        <Section>
          <OrderTitle>Conversation</OrderTitle>
          {order.conversation_id && <OrderConversation conversationId={order.conversation_id} userId={order.user_id}
                                                       onImageClick={onImageClick}/>}
          {replyContent}
        </Section>
      </Card>
    </>;
  }

  return <>
    <CompassHeader pageType={PageType.Orders}/>
    <PageWrapper>
      <SubmittingOverlay show={submitting}/>
      <Page>
        <PageContainer>
          {content}
        </PageContainer>
      </Page>
    </PageWrapper>
  </>;
};

const PageWrapper = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  overflow-y: scroll;
`;

const PageContainer = styled.div`
  width: 100%;
  max-width: 1200px;
  min-height: 100%;
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin: 0 auto;
  position: relative;

  @media (${DeviceQueries.mobile}) {
    padding: 0;
  }
`;

const Card = styled.div`
  background: white;
  overflow: hidden;

  @media screen and (${DeviceQueries.notMobile}) {
    border-radius: 16px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
    border: 1px solid #E4E7EF;
  }
`;

const CardHeader = styled.div`
  padding: 24px 32px;
  background: linear-gradient(to bottom, #FAFAFC, #ffffff);
  border-bottom: 1px solid #E4E7EF;
  display: flex;
  gap: 10px;
  justify-content: space-between;
  align-items: center;

  @media (${DeviceQueries.mobile}) {
    flex-direction: column;
  }
`;

const OrderTitle = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: #1A1D2B;
  margin: 0;
`;

const StatusBadge = styled.div<{ $color: string }>`
  display: inline-flex;
  align-items: center;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  color: white;
  background-color: ${props => props.$color};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const TwoColumn = styled.div`
  display: flex;
  gap: 10px;
  justify-content: space-between;

  @media (${DeviceQueries.mobile}) {
    flex-direction: column-reverse;
  }
`;

const Section = styled.div`
  padding: 24px 32px;
  position: relative;
`;

const SectionTitle = styled.h2`
  font-size: 16px;
  font-weight: 600;
  color: #5A6178;
  margin: 0 0 16px 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const DataTable = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 200px 1fr;
  gap: 16px;
  padding: 12px 0;
  border-bottom: 1px solid #F0F1F6;

  &:last-child {
    border-bottom: none;
  }

  @media (${DeviceQueries.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: #5A6178;
  display: flex;
  align-items: center;
`;

const Value = styled.div<{ $highlight?: boolean }>`
  font-size: 14px;
  color: ${props => props.$highlight ? '#10b981' : '#1A1D2B'};
  font-weight: ${props => props.$highlight ? '600' : '400'};
  display: flex;
  align-items: center;
  word-break: break-word;
  gap: 10px;
`;

const PriceInput = styled(StyledInput)`
  width: 8em;
`;

const UserId = styled.code`
  background: #F0F1F6;
  padding: 6px 10px;
  border-radius: 6px;
  font-size: 13px;
  font-family: 'SF Mono', 'Monaco', 'Courier New', monospace;
  color: #5A6178;
`;

const Divider = styled.div`
  height: 1px;
  background: #E4E7EF;
  margin: 0 32px;
`;

const ReplyRows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ReplyInputContainer = styled.form`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const ReplyInput = styled(TextareaAutosize)`
  flex: 1;
  padding: 12px 16px;
  border: 2px solid #E4E7EF;
  border-radius: 8px;
  font-size: 14px;
  color: #1A1D2B;
  background: white;
  transition: all 0.2s ease;

  &::placeholder {
    color: #8890A5;
  }

  &:focus {
    outline: none;
    border-color: #008AFF;
    box-shadow: 0 0 0 3px rgba(0, 138, 255, 0.1);
  }

  &:hover:not(:focus) {
    border-color: #CDD1DC;
  }
`;

const ProductImage = styled.img`
  margin: auto;
  height: 400px;
  width: auto;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 20px;

  @media (${DeviceQueries.mobile}) {
    flex-direction: column;
  }
`;

const CenteredContainer = styled.div`
  width: 100%;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: space-around;
`;

const AddressList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const AddressWrapper = styled.div``;

const ConversationSecondaryActions = styled.div`
  display: flex;
  justify-content: space-between;
`;

const AssignWarning = styled.div`
  font-style: italic;
  font-size: 75%;
`;

const AddImageContainer = styled.div`
  cursor: pointer;
`;

const SmallUploadedImage = styled.img`
  height: 45px;
  border-radius: 2px;
`;

const TitleRow = styled.div`
  display: flex;
  justify-content: space-between;
`;

const IconWrapper = styled.div`
  cursor: pointer;
`;

const NotCapitalizedDropdownItem = styled(DropdownItem)`
  text-transform: none;
`;

export default OrderPage;
